import { useState, useEffect, useCallback } from 'react';
import { ClientProfile } from '../../../types';
import { useCrmData } from '../../../contexts/CrmProviders';
import { useAuth } from '../../../contexts/AuthContext';

export const useProfileForm = () => {
    const { clientProfile, saveClientProfile, subscription } = useCrmData();
    const { user } = useAuth();
    const [formData, setFormData] = useState<ClientProfile | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    // Sincroniza formData quando o perfil carrega do servidor
    useEffect(() => {
        if (clientProfile) {
            setFormData(clientProfile);
            setIsDirty(false);
        }
    }, [clientProfile]);

    // Handler genérico para inputs — suporta campos aninhados (ex: address.city)
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => {
            if (!prev) return prev;
            if (name.startsWith('address.')) {
                const field = name.split('.')[1];
                return {
                    ...prev,
                    address: { ...prev.address, [field]: value }
                };
            }
            return { ...prev, [name]: value };
        });
        setIsDirty(true);
    }, []);

    const handleSave = useCallback(async () => {
        if (!formData) return;
        setIsSaving(true);
        try {
            await saveClientProfile(formData);
            setIsDirty(false);
        } finally {
            setIsSaving(false);
        }
    }, [formData, saveClientProfile]);

    const handleCancel = useCallback(() => {
        if (clientProfile) {
            setFormData(clientProfile);
            setIsDirty(false);
        }
    }, [clientProfile]);

    return {
        formData,
        user,
        subscription,
        isSaving,
        isDirty,
        handleChange,
        handleSave,
        handleCancel,
    };
};
